import { useMemo } from "react";
import { Box, Typography } from "@mui/material";
import { InvoiceCustomStyle } from "@components/invoice-customization/types/invoiceCustomStyle.types";

interface PaymentMethodPreview {
  provider?: string;
  bankTransferInfo?: {
    accountHolderName?: string;
    routingNumber?: string;
    accountNumber?: string;
  };
}

interface ExtraLineItem {
  title: string;
  amount: string;
  description?: string;
}

interface ServiceLinePreview {
  jobId?: number;
  eventUid?: string;
  qty?: number;
  unitPrice?: number;
  lineTotal?: number;
}

interface InvoiceServiceSummaryPreview {
  serviceName?: string;
  serviceDescription?: string;
  typeOfPlace?: string;
  frequency?: string;
  squareFootage?: string | number;
  areas?: string[];
  specialRequests?: string[];
  additionalObjects?: string[];
  quantity?: number;
  unitPrice?: number;
  serviceLines?: ServiceLinePreview[];
  mainAreasTotal?: number;
  serviceFeeTotal?: number;
  specialRequestsTotal?: number;
  additionalObjectsTotal?: number;
  otherAreasTotal?: number;
  total?: number;
  currency?: string;
}

interface InvoiceEmailPreviewProps {
  invoiceCustomStyle: InvoiceCustomStyle;
  canPay?: boolean;
  invoiceNumber?: string;
  amount?: string;
  dueDate?: string;
  dashboardUrl?: string;
  serviceFeeLabel?: string;
  notes?: string;
  paymentMethods: PaymentMethodPreview[];
  extraLineItems?: ExtraLineItem[];
  serviceFees?: ExtraLineItem[];
  discounts?: ExtraLineItem[];
  invoiceServiceSummary?: InvoiceServiceSummaryPreview;
  logoUrl?: string;
  companyName?: string;
  companyEmail?: string;
  companyAddress?: string;
}

const invoiceFontFamilyMap: Record<string, string> = {
  Inter: "Inter, Arial, sans-serif",
  Roboto: "Roboto, Arial, sans-serif",
  Lato: "Lato, Arial, sans-serif",
  Montserrat: "Montserrat, Arial, sans-serif",
  Merriweather: "Merriweather, Georgia, serif",
  "Source Sans 3": '"Source Sans 3", Arial, sans-serif',
};

const resolveFontFamily = (fontFamily?: string) => {
  const normalized = String(fontFamily ?? "").trim();

  if (!normalized) {
    return "Inter, Arial, sans-serif";
  }

  return invoiceFontFamilyMap[normalized] ?? normalized;
};

const formatMoney = (value?: number | null, currency = "USD") => {
  return `${currency} ${Number(value ?? 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const formatText = (value?: string | number | null) => {
  const normalized = String(value ?? "").trim();
  if (!normalized) return "";

  const map: Record<string, string> = {
    by_weekly: "Bi-weekly",
    by_monthly: "Bi-monthly",
    weekly: "Weekly",
    monthly: "Monthly",
    one_time: "One time",
    unique: "Custom price",
    per_floor: "Per floor",
    per_item: "Per item",
  };

  return map[normalized.toLowerCase()] || normalized.replace(/_/g, " ");
};

const cleanBankValue = (value?: string | number | null) => {
  const text = String(value ?? "").trim();
  if (!text) return "";

  const normalized = text.toLowerCase().replace(/\s+/g, "");
  const invalidValues = ["n/a", "na", "none", "null", "undefined", "-", "--", "n.a", "n.a.", "notavailable", "notapplicable"];

  if (invalidValues.includes(normalized)) return "";
  return text;
};

const maskBankValue = (value?: string | number | null) => {
  const cleanValue = cleanBankValue(value);
  const text = cleanValue.replace(/\s+/g, "");
  if (!text) return "";
  return `•••• ${text.slice(-4)}`;
};

const getPaymentMethodLabels = (paymentMethods: PaymentMethodPreview[]) => {
  return Array.isArray(paymentMethods)
    ? paymentMethods
        .map((method) => {
          const provider = String(method?.provider ?? "").trim();

          if (provider === "bank_transfer") {
            const holder = cleanBankValue(method?.bankTransferInfo?.accountHolderName);
            const routing = maskBankValue(method?.bankTransferInfo?.routingNumber);
            const account = maskBankValue(method?.bankTransferInfo?.accountNumber);

            const shouldShowHolder = holder && holder.toLowerCase() !== "bank transfer";

            return [
              "Bank Transfer",
              shouldShowHolder ? `Holder: ${holder}` : "",
              routing ? `Routing: ${routing}` : "",
              account ? `Account: ${account}` : "",
            ]
              .filter(Boolean)
              .join(" | ");
          }

          const map: Record<string, string> = {
            paypal: "PayPal",
            stripe: "Credit/Debit Card",
            manual_entry: "Manual Payment",
          };

          return map[provider] || formatText(provider);
        })
        .filter(Boolean)
    : [];
};

const CellLabel = ({ children, color }: { children: React.ReactNode; color: string }) => (
  <Typography
    sx={{
      fontSize: "11px",
      fontWeight: 600,
      color,
      margin: "0 0 4px",
      lineHeight: 1.2,
    }}
  >
    {children}
  </Typography>
);

const CellValue = ({ children, color }: { children: React.ReactNode; color: string }) => (
  <Typography
    sx={{
      fontSize: "13px",
      fontWeight: 700,
      color,
      margin: 0,
      lineHeight: 1.3,
      wordBreak: "break-word",
    }}
  >
    {children}
  </Typography>
);

export const InvoiceEmailPreview = ({
  canPay = true,
  invoiceNumber = "",
  amount = "",
  dueDate,
  dashboardUrl = "#",
  serviceFeeLabel = "",
  notes = "",
  paymentMethods,
  extraLineItems = [],
  serviceFees = [],
  discounts = [],
  invoiceServiceSummary,
  logoUrl,
  companyName = "Flownect",
  companyEmail,
  invoiceCustomStyle,
  companyAddress,
}: InvoiceEmailPreviewProps) => {
  const { colors, borders, spacing } = invoiceCustomStyle;
  const typography = {
    fontFamily: resolveFontFamily(invoiceCustomStyle.typography.fontFamily),
    headingFontFamily: resolveFontFamily(invoiceCustomStyle.typography.headingFontFamily),
  };

  console.log("invoice summary: ", invoiceServiceSummary);

  const calculationGroups = useMemo(() => {
    if (!invoiceServiceSummary) return [];

    const serviceLines = Array.isArray(invoiceServiceSummary.serviceLines)
      ? invoiceServiceSummary.serviceLines.filter((l) => l && Number(l.lineTotal ?? l.unitPrice ?? 0) > 0)
      : [];

    if (serviceLines.length === 0) return [];

    const map = new Map<number, number>();
    for (const l of serviceLines) {
      const price = Number(l.unitPrice ?? 0);
      const qty = Number(l.qty ?? 1);
      const roundedKey = Math.round(price * 100) / 100;
      map.set(roundedKey, (map.get(roundedKey) || 0) + qty);
    }

    const groups: Array<{ qty: number; unitPrice: number }> = [];
    map.forEach((qty, unitPrice) => {
      groups.push({ qty, unitPrice });
    });

    return groups;
  }, [invoiceServiceSummary]);

  const totalVisitsCount = useMemo(() => {
    return calculationGroups.reduce((sum, g) => sum + g.qty, 0);
  }, [calculationGroups]);

  const calculationDisplay = useMemo(() => {
    if (!invoiceServiceSummary) return null;

    if (calculationGroups.length === 1) {
      const g = calculationGroups[0];
      return `${g.qty} × ${formatMoney(g.unitPrice, invoiceServiceSummary.currency)}`;
    }

    const qty = Number(invoiceServiceSummary.quantity ?? 0);
    const unitPrice = Number(invoiceServiceSummary.unitPrice ?? 0);
    if (qty > 0 && unitPrice > 0) {
      return `${qty} × ${formatMoney(unitPrice, invoiceServiceSummary.currency)}`;
    }

    return null;
  }, [invoiceServiceSummary, calculationGroups]);

  const paymentMethodLabels = getPaymentMethodLabels(paymentMethods);
  const subtitle = canPay ? "COMPLETE YOUR PAYMENT" : "VIEW YOUR INVOICE";
  const buttonLabel = canPay ? "Pay Invoice" : "View Invoice Details";
  const message = canPay
    ? "Your invoice is ready. Review the service details and complete your payment securely."
    : "Your invoice is ready to view. You have read-only access and cannot complete payment from this email.";

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        py: 2,
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: "500px",
          overflow: "hidden",
          background: colors.cardBackground,
          borderRadius: `${borders.radius}px`,
          fontFamily: typography.fontFamily,
        }}
      >
        {/* Preview del correo: usa la marca de la compañía igual que el template del backend. */}
        <Box
          sx={{
            backgroundColor: colors.emailHeader,
            textAlign: "center",
            px: 2,
            py: 5,
            borderRadius: `${borders.radius}px ${borders.radius}px 0 0`,
          }}
        >
          {logoUrl ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",

                mb: 1.5,
              }}
            >
              <Box
                component="img"
                src={logoUrl}
                alt={companyName}
                sx={{
                  display: "block",
                  width: 72,
                  height: 72,
                  objectFit: "contain",
                  backgroundColor: "transparent",
                  border: "none",
                }}
              />
            </Box>
          ) : null}

          <Typography
            sx={{
              color: colors.title,
              fontFamily: typography.headingFontFamily,
              fontSize: "24px",
              fontWeight: 800,
              lineHeight: 1.2,
              letterSpacing: "-0.02em",
            }}
          >
            {companyName}
          </Typography>

          {companyEmail ? (
            <Typography
              sx={{
                color: colors.body,
                fontSize: "13px",
                margin: "8px 0 0",
                lineHeight: 1.3,
              }}
            >
              {companyEmail}
            </Typography>
          ) : null}
        </Box>

        {/* CONTENT */}
        <Box
          sx={{
            padding: "20px",
            textAlign: "center",
            bgcolor: colors.cardBackground,
          }}
        >
          <Box sx={{ margin: "20px auto 40px" }}>
            <Typography
              sx={{
                color: colors.subTitle,
                fontFamily: typography.headingFontFamily,
                fontSize: "20px",
                fontWeight: 600,
                margin: "0 0 8px",
                lineHeight: 1.3,
              }}
            >
              Invoice Ready
            </Typography>

            <Typography
              sx={{
                color: colors.body,
                fontSize: "16px",
                margin: 0,
                lineHeight: 1.3,
              }}
            >
              {subtitle}
            </Typography>
          </Box>

          {(invoiceNumber || amount) && (
            <Box sx={{ marginBottom: `${spacing.cardGap}px` }}>
              <Box
                component="table"
                sx={{
                  width: "100%",
                  borderCollapse: "collapse",
                  textAlign: "center",
                }}
              >
                <Box component="tbody">
                  <Box component="tr">
                    {invoiceNumber ? (
                      <Box
                        component="td"
                        sx={{
                          padding: "0 12px",
                          borderRight: amount ? `${borders.width}px ${borders.style} ${borders.color || colors.border}` : "none",
                          verticalAlign: "top",
                        }}
                      >
                        <CellLabel color={colors.subTitle}>Invoice</CellLabel>
                        <CellValue color={colors.body}>{invoiceNumber}</CellValue>
                      </Box>
                    ) : null}

                    {amount ? (
                      <Box
                        component="td"
                        sx={{
                          padding: "0 12px",
                          verticalAlign: "top",
                        }}
                      >
                        <CellLabel color={colors.subTitle}>Amount</CellLabel>
                        <CellValue color={colors.total}>{amount}</CellValue>
                      </Box>
                    ) : null}
                  </Box>
                </Box>
              </Box>
            </Box>
          )}

          <Typography
            sx={{
              fontSize: "14px",
              lineHeight: 1.6,
              color: colors.body,
              margin: `0 0 ${spacing.cardGap}px`,
            }}
          >
            {message}
          </Typography>

          {invoiceServiceSummary ? (
            <Box
              sx={{
                textAlign: "left",
                background: colors.pageBackground,
                borderRadius: `${borders.radius}px`,
                padding: "16px",
                margin: `0 0 ${spacing.cardGap}px`,
                width: "100%",
                boxSizing: "border-box",
              }}
            >
              <Typography
                sx={{
                  margin: "0 0 12px",
                  color: colors.title,
                  fontSize: "12px",
                  fontWeight: 800,
                  letterSpacing: "0.04em",
                  textTransform: "uppercase",
                  lineHeight: 1.2,
                }}
              >
                Invoice Details
              </Typography>

              {invoiceServiceSummary.serviceName ? (
                <Typography
                  sx={{
                    margin: "0 0 4px",
                    color: colors.subTitle,
                    fontSize: "15px",
                    fontWeight: 800,
                    lineHeight: 1.3,
                  }}
                >
                  {invoiceServiceSummary.serviceName}
                </Typography>
              ) : null}

              {invoiceServiceSummary.serviceDescription ? (
                <Typography
                  sx={{
                    margin: "0 0 12px",
                    color: colors.subTitle,
                    fontSize: "13px",
                    lineHeight: 1.5,
                  }}
                >
                  {invoiceServiceSummary.serviceDescription}
                </Typography>
              ) : null}

              <Box
                component="table"
                sx={{
                  width: "100%",
                  borderCollapse: "collapse",
                  marginBottom: "12px",
                }}
              >
                <Box component="tbody">
                  {invoiceServiceSummary.typeOfPlace ? (
                    <Box component="tr">
                      <Box
                        component="td"
                        sx={{
                          padding: "4px 0",
                          color: colors.body,
                          fontSize: "13px",
                        }}
                      >
                        Place
                      </Box>
                      <Box
                        component="td"
                        sx={{
                          padding: "4px 0",
                          color: colors.subTitle,
                          fontSize: "13px",
                          fontWeight: 700,
                          textAlign: "right",
                        }}
                      >
                        {invoiceServiceSummary.typeOfPlace}
                      </Box>
                    </Box>
                  ) : null}

                  {invoiceServiceSummary.frequency ? (
                    <Box component="tr">
                      <Box
                        component="td"
                        sx={{
                          padding: "4px 0",
                          color: colors.body,
                          fontSize: "13px",
                        }}
                      >
                        Frequency
                      </Box>
                      <Box
                        component="td"
                        sx={{
                          padding: "4px 0",
                          color: colors.subTitle,
                          fontSize: "13px",
                          fontWeight: 700,
                          textAlign: "right",
                        }}
                      >
                        {formatText(invoiceServiceSummary.frequency)}
                      </Box>
                    </Box>
                  ) : null}

                  {invoiceServiceSummary.squareFootage ? (
                    <Box component="tr">
                      <Box
                        component="td"
                        sx={{
                          padding: "4px 0",
                          color: colors.body,
                          fontSize: "13px",
                        }}
                      >
                        Square footage
                      </Box>
                      <Box
                        component="td"
                        sx={{
                          padding: "4px 0",
                          color: colors.subTitle,
                          fontSize: "13px",
                          fontWeight: 700,
                          textAlign: "right",
                        }}
                      >
                        {String(invoiceServiceSummary.squareFootage)} sqft
                      </Box>
                    </Box>
                  ) : null}
                </Box>
              </Box>

              {invoiceServiceSummary.areas?.length ? (
                <Box sx={{ marginBottom: "12px" }}>
                  <Typography
                    sx={{
                      margin: "0 0 6px",
                      color: colors.subTitle,
                      fontSize: "13px",
                      fontWeight: 700,
                    }}
                  >
                    Areas included
                  </Typography>
                  <Typography
                    sx={{
                      margin: 0,
                      color: colors.body,
                      fontSize: "13px",
                      lineHeight: 1.5,
                    }}
                  >
                    {invoiceServiceSummary.areas.join(", ")}
                  </Typography>
                </Box>
              ) : null}

              {invoiceServiceSummary.specialRequests?.length ? (
                <Box sx={{ marginBottom: "12px" }}>
                  <Typography
                    sx={{
                      margin: "0 0 6px",
                      color: colors.subTitle,
                      fontSize: "13px",
                      fontWeight: 700,
                    }}
                  >
                    Special requests
                  </Typography>
                  <Typography
                    sx={{
                      margin: 0,
                      color: colors.body,
                      fontSize: "13px",
                      lineHeight: 1.5,
                    }}
                  >
                    {invoiceServiceSummary.specialRequests.join(", ")}
                  </Typography>
                </Box>
              ) : null}

              {invoiceServiceSummary.additionalObjects?.length ? (
                <Box sx={{ marginBottom: "12px" }}>
                  <Typography
                    sx={{
                      margin: "0 0 6px",
                      color: colors.subTitle,
                      fontSize: "13px",
                      fontWeight: 700,
                    }}
                  >
                    Additional objects
                  </Typography>
                  <Typography
                    sx={{
                      margin: 0,
                      color: colors.body,
                      fontSize: "13px",
                      lineHeight: 1.5,
                    }}
                  >
                    {invoiceServiceSummary.additionalObjects.join(", ")}
                  </Typography>
                </Box>
              ) : null}

              <Box
                sx={{
                  background: colors.cardBackground,
                  border: `${borders.width}px ${borders.style} ${borders.color || colors.border}`,
                  borderRadius: `${borders.radius}px`,
                  padding: "12px",
                }}
              >
                <Box
                  component="table"
                  sx={{
                    width: "100%",
                    borderCollapse: "collapse",
                  }}
                >
                  <Box component="tbody">
                    {calculationGroups.length > 1 ? (
                      <>
                        <Box component="tr">
                          <Box
                            component="td"
                            colSpan={2}
                            sx={{
                              padding: "2px 0 6px 0",
                              color: colors.title,
                              fontSize: "11px",
                              fontWeight: 800,
                              letterSpacing: "0.04em",
                              textTransform: "uppercase",
                              borderBottom: `${borders.width}px dashed ${colors.border || "var(--fx-surface-border, #E2E8F0)"}`,
                            }}
                          >
                            Visits Breakdown ({totalVisitsCount} visits)
                          </Box>
                        </Box>

                        {calculationGroups.map((group, idx) => (
                          <Box component="tr" key={idx}>
                            <Box
                              component="td"
                              sx={{
                                padding: "4px 0 4px 6px",
                                color: colors.body,
                                fontSize: "12px",
                              }}
                            >
                              {group.qty} × {formatMoney(group.unitPrice, invoiceServiceSummary.currency)}
                            </Box>
                            <Box
                              component="td"
                              sx={{
                                padding: "4px 0",
                                color: colors.total,
                                fontSize: "12px",
                                fontWeight: 600,
                                textAlign: "right",
                              }}
                            >
                              {formatMoney(group.qty * group.unitPrice, invoiceServiceSummary.currency)}
                            </Box>
                          </Box>
                        ))}

                        <Box component="tr">
                          <Box
                            component="td"
                            colSpan={2}
                            sx={{
                              padding: "2px 0 6px 0",
                              borderBottom: `${borders.width}px solid ${colors.border || "var(--fx-surface-border, #E2E8F0)"}`,
                            }}
                          />
                        </Box>
                      </>
                    ) : calculationDisplay ? (
                      <Box component="tr">
                        <Box
                          component="td"
                          sx={{
                            padding: "4px 0",
                            color: colors.body,
                            fontSize: "13px",
                          }}
                        >
                          Calculation
                        </Box>
                        <Box
                          component="td"
                          sx={{
                            padding: "4px 0",
                            color: colors.total,
                            fontSize: "13px",
                            fontWeight: 700,
                            textAlign: "right",
                          }}
                        >
                          {calculationDisplay}
                        </Box>
                      </Box>
                    ) : null}

                    {Number(invoiceServiceSummary.mainAreasTotal ?? 0) > 0 ? (
                      <Box component="tr">
                        <Box
                          component="td"
                          sx={{
                            padding: "4px 0",
                            color: colors.body,
                            fontSize: "13px",
                          }}
                        >
                          Main areas
                        </Box>
                        <Box
                          component="td"
                          sx={{
                            padding: "4px 0",
                            color: colors.total,
                            fontSize: "13px",
                            fontWeight: 700,
                            textAlign: "right",
                          }}
                        >
                          {formatMoney(invoiceServiceSummary.mainAreasTotal, invoiceServiceSummary.currency)}
                        </Box>
                      </Box>
                    ) : null}

                    {Number(invoiceServiceSummary.specialRequestsTotal ?? 0) > 0 ? (
                      <Box component="tr">
                        <Box
                          component="td"
                          sx={{
                            padding: "4px 0",
                            color: colors.subTitle,
                            fontSize: "13px",
                          }}
                        >
                          Special requests
                        </Box>

                        <Box
                          component="td"
                          sx={{
                            padding: "4px 0",
                            color: colors.total,
                            fontSize: "13px",
                            fontWeight: 700,
                            textAlign: "right",
                          }}
                        >
                          {formatMoney(invoiceServiceSummary.specialRequestsTotal, invoiceServiceSummary.currency)}
                        </Box>
                      </Box>
                    ) : null}

                    {Number(invoiceServiceSummary.additionalObjectsTotal ?? 0) > 0 ? (
                      <Box component="tr">
                        <Box
                          component="td"
                          sx={{
                            padding: "4px 0",
                            color: colors.subTitle,
                            fontSize: "13px",
                          }}
                        >
                          Additional objects
                        </Box>

                        <Box
                          component="td"
                          sx={{
                            padding: "4px 0",
                            color: colors.body,
                            fontSize: "13px",
                            fontWeight: 700,
                            textAlign: "right",
                          }}
                        >
                          {formatMoney(invoiceServiceSummary.additionalObjectsTotal, invoiceServiceSummary.currency)}
                        </Box>
                      </Box>
                    ) : null}

                    {Number(invoiceServiceSummary.otherAreasTotal ?? 0) > 0 ? (
                      <Box component="tr">
                        <Box
                          component="td"
                          sx={{
                            padding: "4px 0",
                            color: colors.subTitle,
                            fontSize: "13px",
                          }}
                        >
                          Other areas
                        </Box>

                        <Box
                          component="td"
                          sx={{
                            padding: "4px 0",
                            color: colors.body,
                            fontSize: "13px",
                            fontWeight: 700,
                            textAlign: "right",
                          }}
                        >
                          {formatMoney(invoiceServiceSummary.otherAreasTotal, invoiceServiceSummary.currency)}
                        </Box>
                      </Box>
                    ) : null}

                    {extraLineItems.length > 0
                      ? extraLineItems.map((item, index) => (
                          <Box component="tr" key={`extra-${item.title}-${index}`}>
                            <Box
                              component="td"
                              sx={{
                                padding: "4px 0",
                                color: colors.subTitle,
                                fontSize: "13px",
                              }}
                            >
                              <Box
                                sx={{
                                  fontWeight: 600,
                                  color: colors.body,
                                }}
                              >
                                {item.title}
                              </Box>

                              {item.description ? (
                                <Box
                                  sx={{
                                    marginTop: "2px",
                                    fontSize: "12px",
                                    color: colors.body,
                                    lineHeight: 1.4,
                                  }}
                                >
                                  {item.description}
                                </Box>
                              ) : null}
                            </Box>

                            <Box
                              component="td"
                              sx={{
                                padding: "4px 0",
                                color: colors.total,
                                fontSize: "13px",
                                fontWeight: 700,
                                textAlign: "right",
                                verticalAlign: "top",
                              }}
                            >
                              {item.amount}
                            </Box>
                          </Box>
                        ))
                      : null}

                    {discounts.length > 0
                      ? discounts.map((item, index) => (
                          <Box component="tr" key={`discount-${item.title}-${index}`}>
                            <Box
                              component="td"
                              sx={{
                                padding: "4px 0",
                                color: colors.subTitle,
                                fontSize: "13px",
                              }}
                            >
                              <Box
                                sx={{
                                  fontWeight: 600,
                                  color: colors.body,
                                }}
                              >
                                {item.title}
                              </Box>

                              {item.description ? (
                                <Box
                                  sx={{
                                    marginTop: "2px",
                                    fontSize: "12px",
                                    color: colors.body,
                                    lineHeight: 1.4,
                                  }}
                                >
                                  {item.description}
                                </Box>
                              ) : null}
                            </Box>

                            <Box
                              component="td"
                              sx={{
                                padding: "4px 0",
                                color: colors.total,
                                fontSize: "13px",
                                fontWeight: 700,
                                textAlign: "right",
                                verticalAlign: "top",
                              }}
                            >
                              {item.amount}
                            </Box>
                          </Box>
                        ))
                      : null}

                    {serviceFees.length > 0
                      ? serviceFees.map((item, index) => (
                          <Box component="tr" key={`fee-${item.title}-${index}`}>
                            <Box
                              component="td"
                              sx={{
                                padding: "4px 0",
                                color: colors.subTitle,
                                fontSize: "13px",
                              }}
                            >
                              <Box
                                sx={{
                                  fontWeight: 600,
                                  color: colors.body,
                                }}
                              >
                                {item.title}
                              </Box>

                              {item.description ? (
                                <Box
                                  sx={{
                                    marginTop: "2px",
                                    fontSize: "12px",
                                    color: colors.body,
                                    lineHeight: 1.4,
                                  }}
                                >
                                  {item.description}
                                </Box>
                              ) : null}
                            </Box>

                            <Box
                              component="td"
                              sx={{
                                padding: "4px 0",
                                color: colors.total,
                                fontSize: "13px",
                                fontWeight: 700,
                                textAlign: "right",
                                verticalAlign: "top",
                              }}
                            >
                              {item.amount}
                            </Box>
                          </Box>
                        ))
                      : Number(invoiceServiceSummary.serviceFeeTotal ?? 0) > 0 ? (
                          <Box component="tr">
                            <Box
                              component="td"
                              sx={{
                                padding: "4px 0",
                                color: colors.body,
                                fontSize: "13px",
                              }}
                            >
                              {serviceFeeLabel || "Service Fee"}
                            </Box>
                            <Box
                              component="td"
                              sx={{
                                padding: "4px 0",
                                color: colors.total,
                                fontSize: "13px",
                                fontWeight: 700,
                                textAlign: "right",
                              }}
                            >
                              {formatMoney(invoiceServiceSummary.serviceFeeTotal, invoiceServiceSummary.currency)}
                            </Box>
                          </Box>
                        ) : null}

                    <Box component="tr">
                      <Box
                        component="td"
                        sx={{
                          padding: "10px 0 0",
                          color: colors.title,
                          fontSize: "14px",
                          fontWeight: 800,
                          borderTop: `${borders.width}px ${borders.style} ${borders.color || colors.border}`,
                        }}
                      >
                        Total
                      </Box>
                      <Box
                        component="td"
                        sx={{
                          padding: "10px 0 0",
                          color: colors.total,
                          fontSize: "15px",
                          fontWeight: 900,
                          textAlign: "right",
                          borderTop: `${borders.width}px ${borders.style} ${borders.color || colors.border}`,
                        }}
                      >
                        {formatMoney(invoiceServiceSummary.total, invoiceServiceSummary.currency)}
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>
          ) : null}

          {notes ? (
            <Box
              sx={{
                textAlign: "left",
                border: `${borders.width}px ${borders.style} ${borders.color || colors.border}`,
                borderRadius: `${borders.radius}px`,
                padding: "14px",
                margin: `0 0 ${spacing.cardGap}px`,
                width: "100%",
                boxSizing: "border-box",
              }}
            >
              <Typography
                sx={{
                  margin: "0 0 8px",
                  color: colors.subTitle,
                  fontSize: "12px",
                  fontWeight: 800,
                  letterSpacing: "0.04em",
                  textTransform: "uppercase",
                }}
              >
                Notes
              </Typography>
              <Typography
                sx={{
                  margin: 0,
                  color: colors.body,
                  fontSize: "13px",
                  lineHeight: 1.6,
                  whiteSpace: "pre-wrap",
                }}
              >
                {notes}
              </Typography>
            </Box>
          ) : null}

          {paymentMethodLabels.length > 0 ? (
            <Box
              sx={{
                textAlign: "left",
                border: `${borders.width}px ${borders.style} ${borders.color || colors.border}`,
                borderRadius: `${borders.radius}px`,
                padding: "14px",
                margin: `0 0 ${spacing.cardGap}px`,
                width: "100%",
                boxSizing: "border-box",
              }}
            >
              <Typography
                sx={{
                  margin: "0 0 8px",
                  color: colors.subTitle,
                  fontSize: "12px",
                  fontWeight: 800,
                  letterSpacing: "0.04em",
                  textTransform: "uppercase",
                }}
              >
                Payment Method
              </Typography>
              <Typography
                sx={{
                  margin: 0,
                  color: colors.body,
                  fontSize: "13px",
                  lineHeight: 1.6,
                }}
              >
                {paymentMethodLabels.join(", ")}
              </Typography>
            </Box>
          ) : null}

          <Box
            sx={{
              my: 2,
              p: 1.5,
              borderRadius: 2,
              backgroundColor: colors.emailHeader,
              textAlign: "start",
            }}
          >
            <Typography
              sx={{
                fontSize: "12px",
                fontWeight: 800,
                color: colors.title,
                mb: 0.75,
              }}
            >
              Pay by check
            </Typography>

            <Typography
              sx={{
                fontSize: "12px",
                color: colors.body,
                lineHeight: 1.5,
              }}
            >
              <strong>Make checks payable to:</strong> {companyName}
            </Typography>

            <Typography
              sx={{
                fontSize: "12px",
                color: colors.body,
                lineHeight: 1.5,
              }}
            >
              <strong>Company address:</strong> {companyAddress}
            </Typography>

            <Typography
              sx={{
                fontSize: "12px",
                color: colors.body,
                lineHeight: 1.5,
                mt: 0.5,
              }}
            >
              Please notify us once the payment has been made.
            </Typography>
          </Box>

          <Box sx={{ marginBottom: `${spacing.cardGap}px` }}>
            <Box
              component="a"
              href={dashboardUrl}
              sx={{
                display: "inline-block",
                background: colors.total,
                color: "#fff",
                fontSize: "14px",
                fontWeight: 700,
                textDecoration: "none",
                padding: "16px 20px",
                borderRadius: `${borders.radius}px`,
              }}
            >
              {buttonLabel}
            </Box>
          </Box>

          {canPay ? (
            <Box
              sx={{
                background: colors.cardBackground,
                borderRadius: `${borders.radius}px`,
                padding: "20px",
                marginTop: "10px",
                width: "80%",
                marginLeft: "auto",
                marginRight: "auto",
              }}
            >
              <Typography
                sx={{
                  color: "var(--fx-surface-text, #0F172A)",
                  fontSize: "13px",
                  fontWeight: 800,
                  margin: "0 0 8px",
                }}
              >
                Secure Payment
              </Typography>
              <Typography
                sx={{
                  color: colors.body,
                  fontSize: "12px",
                  lineHeight: 1.5,
                  margin: 0,
                }}
              >
                All transactions are encrypted and processed securely.
                <br />
                Your financial information is never shared with us.
              </Typography>
            </Box>
          ) : null}
        </Box>

        {/* FOOTER */}
        <Box
          sx={{
            background: colors.emailHeader,
            textAlign: "center",
            px: 2,
            py: 3,
            borderRadius: `0 0 ${borders.radius}px ${borders.radius}px`,
          }}
        >
          {companyEmail ? (
            <Typography
              sx={{
                fontSize: "13px",
                fontWeight: 700,
                lineHeight: 1.5,
                mb: 1.5,
                color: colors.title,
              }}
            >
              <Box component="span" sx={{ color: colors.subTitle }}>
                Need help? Contact{" "}
              </Box>
              {companyEmail}
            </Typography>
          ) : null}

          <Box
            sx={{
              width: "80%",
              height: "1px",
              background: colors.border,
              mx: "auto",
              mb: 1.5,
            }}
          />

          <Typography
            sx={{
              color: colors.body,
              fontSize: "12px",
              lineHeight: 1.4,
              mb: 1.5,
            }}
          >
            © {new Date().getFullYear()} {companyName}. All rights reserved.
          </Typography>

          <Typography
            sx={{
              color: colors.body,
              fontSize: "11px",
              lineHeight: 1.4,
            }}
          >
            If you didn't expect this invoice, you can safely ignore this email.
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};
